import lowerCase from 'lodash/lowerCase'
import { useCallback } from 'react'

import { ContactType } from '../../contacts/types/contactType'
import { ProductType } from '../../products/enums/productType'
import { TaxRateGroup } from '../types/taxRateGroup'
import { TaxRuleContactType } from '../types/taxRuleContactType'
import { TaxRuleProductType } from '../types/taxRuleProductType'
import { VatRate } from '../types/vatRate'
import { getVatRatesSortedByPriorities } from '../utils/getVatRatesSortedByPriorities'
import { useGetRegionFromContactCountryCode } from './useGetRegionFromContactCountryCode'
import { useGetVatRates } from './useGetVatRates'

interface GetFilteredVatRatesListOptions {
  contactType?: TaxRuleContactType
  productType?: TaxRuleProductType
  region?: TaxRateGroup
  vatRates: VatRate[]
}

const getFilteredVatRatesList = ({
  contactType,
  productType,
  region,
  vatRates,
}: GetFilteredVatRatesListOptions): VatRate[] => {
  return (
    vatRates?.filter((vatRate) =>
      // we need to check if any of the tag combinations fits the given params
      vatRate.tags.some((tagsCombination) => {
        // - taxRateGroup should be included in tags in many cases, but it's not guaranteed for all
        // - we don't need to verify if a value is undefined, which automatically satisfies
        //   a condition (e.g. if we don't have organization or receiver contact data,
        //   the region is undefined, thus we can't use it for filtering)
        const tagValues = [undefined, vatRate.taxRateGroup, ...tagsCombination]

        const isValidRegion = tagValues.includes(region)
        const isValidContactType = tagValues.includes(contactType)
        const isValidProductType = tagValues.includes(productType)

        return isValidRegion && isValidContactType && isValidProductType
      }),
    ) || []
  )
}

interface GetFilteredVatRatesOptions {
  contactCountryCode?: string
  contactType?: ContactType
  productType?: ProductType
}

export const useFilteredVatRates = () => {
  const { vatRates: allVatRates, isLoading } = useGetVatRates()
  const { getRegionFromContactCountryCode } = useGetRegionFromContactCountryCode()

  const getFilteredVatRates = useCallback(
    ({ contactCountryCode, contactType: receiverContactType, productType }: GetFilteredVatRatesOptions): VatRate[] => {
      const region = getRegionFromContactCountryCode(contactCountryCode)
      const contactType: TaxRuleContactType | undefined =
        receiverContactType === ContactType.Company || receiverContactType === ContactType.Person
          ? receiverContactType
          : undefined

      const filteredVatRates = getFilteredVatRatesList({
        contactType,
        productType: (lowerCase(productType) as TaxRuleProductType) || undefined,
        region,
        vatRates: allVatRates,
      })

      return isLoading ? [] : getVatRatesSortedByPriorities(filteredVatRates)
    },
    [allVatRates, getRegionFromContactCountryCode, isLoading],
  )

  return {
    getFilteredVatRates,
    isLoading,
  }
}
